import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import type { Occupancy, Period } from 'lumen/models';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';
import { formatOverbookingValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface HotelPricingAssistantTableTooltipsTooltipOverbookingTableSignature {
  Args: {
    period: Period;
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipOverbookingTable extends Component<HotelPricingAssistantTableTooltipsTooltipOverbookingTableSignature> {
  @service declare intl: Services['intl'];

  get failedOverbookingValue(): number | null {
    return this.args.period.failedOverbookingValue;
  }

  get formattedOverbookingValue(): number | string {
    return formatOverbookingValue({
      intl: this.intl,
      value: this.overbooking,
    });
  }

  get hasOverbookingOverride(): boolean {
    return this.args.period.hasOverbookingOverride;
  }

  get isLoading(): boolean {
    return this.args.period.hasOverbookingActionInProgress;
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  get overbooking(): number | null {
    return this.args.period.effectiveOverbookingWithOverride;
  }

  get overbookingRecommendationRow(): number | string {
    const recommendedValue =
      this.args.period.hotelLevelRestrictionRecommendation?.overbookingValue;

    if (recommendedValue == null || recommendedValue === this.overbooking) {
      return this.intl.t('pricing_manager.restrictions.already_optimal');
    }

    return formatOverbookingValue({
      intl: this.intl,
      value: recommendedValue,
    });
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking_tooltip.physical_rooms_available',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking_tooltip.physical_rooms_available',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get physicalRoomsAvailableValue(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get roomsForSaleLabel(): string {
    return this.intl.t('pricing_manager.overbooking.room_for_sale', {
      overbooking: this.formattedOverbookingValue,
    });
  }

  get roomsForSaleValue(): string | number {
    if (
      this.occupancyData &&
      this.occupancyData.availableRooms != null &&
      this.overbooking !== null
    ) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking,
      );
    }

    return this.intl.t('general.missing');
  }

  get showFailedOverbooking(): boolean {
    return this.args.period.shouldShowOverbookingException;
  }

  get showOverrideIndicator(): boolean {
    return this.hasOverbookingOverride && !this.showFailedOverbooking;
  }

  <template>
    <table class="default-table {{styles.table}}">
      <thead>
        <tr>
          <th class="{{styles.table-header}} text-200--bold">
            {{t "rates.booking_curve.inventory"}}
          </th>
        </tr>
      </thead>
      <tbody>
        {{! Row for Physical rooms available }}
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            <p
              class="text-200--regular"
            >{{this.physicalRoomsAvailableLabel}}</p>
            <div class="flex items-center">
              <p
                class="text-200--regular"
              >{{this.physicalRoomsAvailableValue}}</p>
              {{#if this.showOverrideIndicator}}<span
                  class="invisible"
                ><OverrideIndicator /></span>{{/if}}
            </div>
          </td>
        </tr>
        {{! Row for rooms for sale }}
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            {{#if this.isLoading}}
              <div class="loading-pulse w-5/6"></div>
              <div class="loading-pulse w-1/6"></div>
            {{else}}
              <p class="text-200--regular">{{this.roomsForSaleLabel}}</p>
              <div class="flex items-center">
                <p class="text-200--regular">{{this.roomsForSaleValue}}</p>
                {{#if this.showOverrideIndicator}}<OverrideIndicator
                    @variant="automation-off"
                  />{{/if}}
              </div>
            {{/if}}
          </td>
        </tr>
        {{! Row for recommended overbooking when override is active }}
        {{#if this.showOverrideIndicator}}
          <tr>
            <td
              class="{{styles.table-row-cell}}
                {{styles.indented-row}}
                text-200--regular"
            >

              <p class="text-200--regular">{{t
                  "pricing_manager.overbooking_tooltip.recommended_overbooking"
                }}</p>
              {{#if this.isLoading}}
                <div class="loading-pulse w-1/6"></div>
              {{else}}

                <div class="flex items-center">
                  <p
                    class="text-200--regular"
                  >{{this.overbookingRecommendationRow}}</p>
                  <span class="invisible"><OverrideIndicator /></span>
                </div>
              {{/if}}
            </td>
          </tr>
        {{/if}}
        {{#if this.showFailedOverbooking}}
          <tr>
            <td class="{{styles.table-row-cell}} text-200--regular">
              <p class="text-200--regular">
                {{t "pricing_manager.failed_overbooking"}}
              </p>
              <p class="text-200--regular">{{this.failedOverbookingValue}}</p>
            </td>
          </tr>
        {{/if}}
      </tbody>
    </table>
  </template>
}
